/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  SxProps,
  TextField,
  useTheme,
} from '@mui/material';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { Spinner } from '../Spinner';
import { Icon } from '../Icon';
import { Tag } from './Tag';

const ADD_NEW_SENTINEL = '__POUNCE_V2_ADD_NEW__';
const isAddNewSentinel = (item: unknown): boolean => item === ADD_NEW_SENTINEL;

// Constants for the virtualized listbox
const OPTION_HEIGHT = 48;
const CLEAR_ALL_HEIGHT = 28;
const MAX_VISIBLE_OPTIONS = 7;

type DefaultContentProps<T> = {
  itemToString: (item: T) => string;
  removeItem: (item: T) => void;
  value: T[];
  isOpen: boolean;
  emptyText?: string;
};

export type MultiComboboxProps<T> = {
  /** Callback when the selection changes */
  onChange: (value: T[]) => void;

  /** The label associated with this dropdown form element */
  label: string;

  /** Whether the label should get visually hidden */
  hideLabel?: boolean;

  /** A list of entries that the dropdown will have as options */
  items: T[];

  /**
   * A function that converts the an item to a string. This is the value that the dropdown will
   * expose to the user. This is also the value that the dropdown will use internally for comparisons
   * so it should be unique (which should be by default since you wouldn't want to expose duplicate
   * values to the user) */
  itemToString?: (item: T) => string;

  /**
   * A function that converts the item to a key used in ComboBoxItems.
   * Useful when you need unique react keys when itemToString isnt necessarily unique
   */
  itemToKey?: (item: T) => string;

  /**
   * A function to render each menu item if customization is needed instead
   * of rendering the item as a string
   */
  renderMenuItem?: (item: T) => React.ReactNode;

  /**
   * A function used to group the Menu Items.
   */
  itemToGroup?: (item: T) => string;

  /**
   * This variable is used to render additional content at the end of the input (useful for icons)
   */
  inputEndContent?: React.ReactNode;

  /**
   * A function that accepts an item as a parameter and returns `true` if the item should be
   * disabled or `false` otherwise. Defaults to `() => false`.
   */
  disableItem?: (item: T) => boolean;

  /**
   * The value of the item that is currently selected. The component is a controlled one,
   * so the the selected value should be provided explicitly to the dropdown
   * */
  value: T[];

  /** Whether the multi-combobox has an invalid value */
  invalid?: boolean;

  /** Whether the MultiCombobox is in a loading state. This is useful when the `items` are coming asynchronously */
  loading?: boolean;

  /** Whether the multi-combobox is required or not */
  required?: boolean;

  /** Override the default tag content */
  renderContent?: ({
    itemToString,
    removeItem,
    value,
    isOpen,
  }: DefaultContentProps<T>) => React.ReactNode;

  /** Whether the multi-combobox is disabled or not */
  disabled?: boolean;

  /** A placeholder that's visible when the user focuses on the MultiCombobox */
  placeholder?: string;

  /** A handler for the blur event on the MultiCombobox's input */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /** A handler for the focus event on the MultiCombobox's input */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /** Callback when the dropdown opens */
  onOpen?: () => void;

  /** Callback when the dropdown closes */
  onClose?: () => void;

  /** The name passed to the underlying input */
  name?: string;

  /** Whether the MultiCombobox should have an input to search results */
  searchable?: boolean;

  /** Whether the MultiCombobox input should be cleared once a value is selected */
  clearInputOnSelection?: boolean;

  /** @ignore */
  hidden?: boolean;

  /**
   * Allow the user to add custom entries to the dropdown instead of limiting selections to the
   * predefined set of options. The `searchable` prop should be true in order for
   * this functionality to work.
   * */
  allowAdditions?: boolean;

  /**
   * The maximum number of results that the MultiCombobox should show. Default value is
   * `undefined` to display which displays all of them.
   * */
  maxResults?: number;

  /** The maximum height (in pixels) of the MultiCombobox dropdown. Defaults to 300. */
  maxHeight?: number;

  /** The maximum height (in pixels) of the MultiCombobox's input. Defaults to 300. Use "none" to disable the cap and allow unlimited growth. */
  maxContainerHeight?: number | 'none';

  /** The maximum width (in pixels) of the MultiCombobox dropdown. Defaults to 800. */
  maxWidth?: number;

  /**
   * A function that runs before a custom item is added by the user. If it returns `true`, then this
   * item will be added to the selection. If not, then this item won't be added
   * */
  validateAddition?: (userEnteredInput: string, existing: T[]) => boolean;

  /**
   * This variable is used to determine if and when we are going show users the ability to clear
   * everything they have added to the MultiCombobox
   */
  canClearAllAfter?: number;

  /**
   * This variable determines if we should show the ability to select all items in the dropdown
   */
  canSelectAll?: boolean;

  /**
   * This variable determines the text that will be displayed for the select all option
   */
  selectAllText?: string | ((results: T[], inputText: string) => string);

  /**
   * Custom text to display when no items are selected. If not provided, shows tags as usual.
   */
  emptyText?: string;

  /**
   * When true, uses `startsWith` instead of `includes` for filtering.
   * Defaults to false for more lenient matching.
   */
  strictSearch?: boolean;

  /** Whether the dropdown should open when the input receives focus. Defaults to false. */
  openOnFocus?: boolean;

  /**
   * Custom styles to apply to the MultiCombobox
   */
  sx?: SxProps;
} & Omit<
  React.HTMLAttributes<HTMLElement>,
  'onChange' | 'children' | 'hidden' | 'defaultValue' | 'defaultChecked' | 'onBlur' | 'onFocus'
>;

const CheckmarkSvg: React.FC<{ checked: boolean }> = ({ checked }) => {
  const {
    palette: { componentTokens },
  } = useTheme();

  const stroke = checked
    ? componentTokens?.selectors?.colors?.checkbox?.selected?.stroke?.resting
    : componentTokens?.selectors?.colors?.checkbox?.notSelected?.stroke?.resting;
  const fill = checked
    ? componentTokens?.selectors?.colors?.checkbox?.selected?.background?.resting
    : componentTokens?.selectors?.colors?.checkbox?.notSelected?.background?.resting;
  const pathFill = componentTokens?.selectors?.colors?.checkbox?.selected?.assets?.base;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      style={{ display: 'block', flexShrink: 0, marginLeft: 8 }}
    >
      <rect
        x={1}
        y={1}
        width={22}
        height={22}
        rx={4}
        ry={4}
        stroke={stroke}
        fill={fill}
        strokeWidth={1.5}
      />
      {checked && (
        <path
          d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z"
          fill={pathFill}
          transform="translate(2, 3) scale(0.85)"
        />
      )}
    </svg>
  );
};

/* ---- Virtualized Listbox (react-window v1) ---- */

function RowRenderer(props: ListChildComponentProps<React.ReactElement[]>) {
  const { data, index, style } = props;
  const child = data[index];

  return React.cloneElement(child, {
    style: {
      ...(child.props.style || {}),
      ...style,
      top: (style.top as number) ?? 0,
    },
  });
}

function useResetCache(itemCount: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [itemCount]);
  return ref;
}

const VirtualizedListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox(props, ref) {
    const {
      children,
      toggleGroup,
      showClearAll,
      clearAll,
      showSelectAll,
      selectAll,
      selectAllText,
      allSelected,
      maxHeight: listMaxHeight,
      ...other
    } = props as any;
    const rawItems = React.Children.toArray(children) as React.ReactElement[];
    const {
      palette: { tokens, typographyTokens, componentTokens },
    } = useTheme();

    const items: React.ReactElement[] = [];

    if (showClearAll) {
      items.push(
        <Box
          component="li"
          key="clear-all"
          data-clear-all="true"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            clearAll?.();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            boxSizing: 'border-box',
            height: 28,
            fontSize: typographyTokens.fontSizes.xs,
            px: 4,
            color: tokens.content.text.accent[3].base,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            sx={{ width: 16, height: 16, flexShrink: 0 }}
          >
            <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
          </Box>
          <Box component="span">Clear All</Box>
        </Box>,
      );
    }

    if (showSelectAll) {
      items.push(
        <Box
          component="li"
          key="select-all"
          data-select-all="true"
          role="option"
          aria-selected={!!allSelected}
          onClick={selectAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '16px',
            cursor: 'pointer',
            boxSizing: 'border-box',
            paddingRight: '16px',
            fontFamily: typographyTokens.fontFamilies.sans,
            fontSize: typographyTokens.fontSizes.sm,
            lineHeight: typographyTokens.lineHeights.sm,
            letterSpacing: typographyTokens.letterSpacings.normal,
            backgroundColor: componentTokens.selectMenuItems.color.item.background.row.resting,
            color: componentTokens.selectMenuItems.color.item.text.row.resting,
            '&:hover': {
              backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
              color: componentTokens.selectMenuItems.color.item.text.row.hover,
            },
            '&:active': {
              backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
              color: componentTokens.selectMenuItems.color.item.text.row.hover,
            },
            '&:focus': {
              backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
              color: componentTokens.selectMenuItems.color.item.text.row.hover,
            },
            ...(allSelected && {
              backgroundColor: componentTokens.selectMenuItems.color.item.background.row.selected,
              color: componentTokens.selectMenuItems.color.item.text.row.selected,
            }),
          }}
        >
          <Box component="span">{selectAllText}</Box>
          <CheckmarkSvg checked={!!allSelected} />
        </Box>,
      );
    }

    rawItems.forEach(child => {
      const childProps = (child as any).props;
      if (childProps?.['data-first-in-group']) {
        const groupName = childProps['data-group-name'] as string;
        const groupAllSelected = childProps['data-all-group-selected'] === 'true';
        items.push(
          <Box
            component="li"
            key={`group-header-${groupName}`}
            data-group-header="true"
            className="MuiAutocomplete-option-header"
            role="option"
            aria-selected={groupAllSelected}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              toggleGroup?.(groupName);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              boxSizing: 'border-box',
              paddingLeft: '16px',
              paddingRight: '16px',
              textTransform: 'uppercase',
              fontFamily: typographyTokens.fontFamilies.sans,
              fontWeight: typographyTokens.fontWeights.semibold,
              fontSize: typographyTokens.fontSizes.sm,
              lineHeight: typographyTokens.lineHeights.sm,
              letterSpacing: typographyTokens.letterSpacings.normal,
              // We're using different colors for header until design is updated
              backgroundColor: tokens.background.neutral.inverse.raisedToBrand[4],
              color: componentTokens.selectMenuItems.color.item.text.header.hover,
              '&:hover': {
                backgroundColor: tokens.background.neutral.inverse.raised[5],
                color: componentTokens.selectMenuItems.color.item.text.header.hover,
              },
              '&:active': {
                backgroundColor: componentTokens.selectMenuItems.color.item.background.row.selected,
                color: componentTokens.selectMenuItems.color.item.text.header.hover,
              },
              '&:focus': {
                backgroundColor: componentTokens.selectMenuItems.color.item.background.row.selected,
                color: componentTokens.selectMenuItems.color.item.text.header.hover,
              },
              ...(groupAllSelected && {
                backgroundColor: componentTokens.selectMenuItems.color.item.background.row.selected,
                color: componentTokens.selectMenuItems.color.item.text.header.selected,
              }),
            }}
          >
            <Box component="span">{groupName}</Box>
            <CheckmarkSvg checked={groupAllSelected} />
          </Box>,
        );
      }
      items.push(child);
    });

    const itemCount = items.length;
    const listRef = useResetCache(itemCount);

    const getItemSize = (index: number) => {
      const child = items[index];
      if ((child as any).props?.['data-clear-all']) {
        return CLEAR_ALL_HEIGHT;
      }
      return OPTION_HEIGHT;
    };

    const getHeight = () => {
      const maxH = listMaxHeight || MAX_VISIBLE_OPTIONS * OPTION_HEIGHT;
      let total = 0;
      for (let i = 0; i < itemCount; i += 1) {
        total += getItemSize(i);
        if (total >= maxH) {
          return maxH;
        }
      }
      return total;
    };

    return (
      <div ref={ref} {...other}>
        <VariableSizeList
          ref={listRef}
          height={getHeight()}
          width="100%"
          itemSize={getItemSize}
          itemCount={itemCount}
          itemData={items}
          overscanCount={5}
          innerElementType="ul"
        >
          {RowRenderer}
        </VariableSizeList>
      </div>
    );
  },
);

/* ---- Component ---- */

function MultiComboboxComponent<Item>(
  {
    onChange,
    value,
    items,
    label = '',
    disabled = false,
    loading = false,
    placeholder = '',
    searchable = false,
    invalid,
    required,
    hideLabel,
    itemToGroup,
    itemToString = item => String(item),
    canClearAllAfter,
    canSelectAll = false,
    selectAllText = 'Select All',
    renderContent,
    renderMenuItem,
    emptyText,
    itemToKey,
    disableItem,
    inputEndContent,
    onBlur,
    onFocus,
    onOpen,
    onClose,
    name,
    hidden,
    maxResults,
    maxHeight,
    maxWidth,
    maxContainerHeight,
    clearInputOnSelection,
    strictSearch,
    allowAdditions,
    validateAddition,
    openOnFocus = false,
    sx,
    ...restProps
  }: MultiComboboxProps<Item>,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const forwardedRef = React.useRef(ref);
  forwardedRef.current = ref;

  const setRef = React.useCallback((el: HTMLDivElement | null) => {
    (rootRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    const r = forwardedRef.current;
    if (typeof r === 'function') {
      r(el);
    } else if (r) {
      (r as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }
  }, []);

  const sortedItems = React.useMemo(() => {
    if (!itemToGroup) {
      return items;
    }
    return [...items].sort((a, b) => itemToGroup(a).localeCompare(itemToGroup(b)));
  }, [items, itemToGroup]);

  // Shared filtering logic used by both filteredItems and filterOptions
  const applyFiltering = React.useCallback(
    (itemsToFilter: Item[], searchQuery: string) => {
      let filtered = itemsToFilter;
      if (searchable && searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = itemsToFilter.filter(item =>
          strictSearch
            ? itemToString(item).toLowerCase().startsWith(query)
            : itemToString(item).toLowerCase().includes(query),
        );
      }
      if (maxResults) {
        filtered = filtered.slice(0, maxResults);
      }
      return filtered;
    },
    [searchable, strictSearch, itemToString, maxResults],
  );

  // Compute filtered items in render (not via ref) to avoid stale state in allSelected
  const filteredItems = React.useMemo(
    () => applyFiltering(sortedItems, inputValue),
    [applyFiltering, sortedItems, inputValue],
  );

  // Only open when there's something to show.
  // When allowAdditions=true, gate on: validateAddition passes AND the value isn't already
  // selected — keeping this in sync with filterOptions ensures we never open to an empty
  // "No options" listbox after suppressing the sentinel for an already-selected value.
  const trimmedInput = inputValue.trim();
  const shouldShowDropdown =
    open &&
    (filteredItems.length > 0 ||
      (trimmedInput.length > 0 &&
        (!allowAdditions ||
          ((!validateAddition || validateAddition(trimmedInput, value)) &&
            !value.some(item => itemToString(item) === trimmedInput)))));

  // Dynamically calculate the minimum width of the popper based on the longest item, this is required since we're using absolute positioning in the virtualized listbox
  const popperMinWidth = React.useMemo(() => {
    if (items.length === 0) {
      return undefined;
    }
    const longest = items.reduce((max, item) => {
      const len = itemToString(item).length;
      return len > max ? len : max;
    }, 0);
    // ~7.5px per character + padding for checkbox + horizontal padding
    const estimated = longest * 7.5 + 60;
    return maxWidth ? Math.min(estimated, maxWidth) : estimated;
  }, [items, itemToString, maxWidth]);

  const removeItem = (item: Item) => {
    onChange(value.filter(i => i !== item));
  };

  // Returns true when the value was consumed (added or already exists), false when rejected.
  // Callers use the return value to decide whether to clear the input: clear on consumed,
  // keep on rejected so the user can correct a validation-blocked entry.
  const commitTypedValue = React.useCallback(
    (trimmed: string): boolean => {
      if (trimmed.length === 0) {
        return false;
      }
      if (value.find(item => itemToString(item) === trimmed) !== undefined) {
        return true;
      }
      if (validateAddition && !validateAddition(trimmed, value)) {
        return false;
      }
      onChange([...value, trimmed as unknown as Item]);
      return true;
    },
    [value, itemToString, validateAddition, onChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!allowAdditions || !searchable) {
        return;
      }
      const { key } = e;
      if (key !== 'Enter' && key !== ',') {
        return;
      }
      const trimmed = ((e.target as HTMLInputElement).value ?? inputValue).trim();
      if (trimmed.length === 0) {
        return;
      }
      e.preventDefault();
      if (commitTypedValue(trimmed)) {
        e.stopPropagation();
        setInputValue('');
      }
    },
    [allowAdditions, searchable, inputValue, commitTypedValue],
  );

  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (!allowAdditions) {
        return;
      }
      const clipboardData = e.clipboardData.getData('Text');
      const pastedStrings = clipboardData
        .replace(/\r?\n/g, ',')
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      const newItems = (
        validateAddition ? pastedStrings.filter(s => validateAddition(s, value)) : pastedStrings
      ).map(s => s as unknown as Item);
      if (pastedStrings.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        onChange([...value, ...newItems]);
      }
    },
    [allowAdditions, validateAddition, value, onChange],
  );

  const clearAll = React.useCallback(() => {
    onChange([]);
  }, [onChange]);

  const showClearAll = open && !!canClearAllAfter && value.length >= canClearAllAfter;

  const selectAll = React.useCallback(() => {
    // Filter out disabled items, same as toggleGroup
    const selectableItems = disableItem
      ? filteredItems.filter(item => !disableItem(item))
      : filteredItems;
    const allSelected =
      selectableItems.length > 0 &&
      selectableItems.every(item => value.some(v => itemToString(v) === itemToString(item)));
    if (allSelected) {
      const selectableStrings = new Set(selectableItems.map(itemToString));
      onChange(value.filter(v => !selectableStrings.has(itemToString(v))));
    } else {
      const currentStrings = new Set(value.map(itemToString));
      const newItems = selectableItems.filter(item => !currentStrings.has(itemToString(item)));
      onChange([...value, ...newItems]);
    }
  }, [filteredItems, value, itemToString, onChange, disableItem]);

  const toggleGroup = React.useCallback(
    (groupName: string) => {
      if (!itemToGroup) {
        return;
      }
      const groupItems = filteredItems.filter(item => itemToGroup(item) === groupName);
      const selectableGroupItems = disableItem
        ? groupItems.filter(item => !disableItem(item))
        : groupItems;
      const allSelected =
        selectableGroupItems.length > 0 &&
        selectableGroupItems.every(item => value.some(v => itemToString(v) === itemToString(item)));
      if (allSelected) {
        const groupStrings = new Set(selectableGroupItems.map(itemToString));
        onChange(value.filter(v => !groupStrings.has(itemToString(v))));
      } else {
        const currentStrings = new Set(value.map(itemToString));
        const newItems = selectableGroupItems.filter(
          item => !currentStrings.has(itemToString(item)),
        );
        onChange([...value, ...newItems]);
      }
    },
    [filteredItems, itemToGroup, itemToString, value, onChange, disableItem],
  );

  return (
    <Autocomplete<Item, true, true, false>
      ref={setRef}
      multiple
      fullWidth
      disableListWrap
      openOnFocus={openOnFocus}
      hidden={hidden}
      sx={sx}
      open={shouldShowDropdown}
      onOpen={() => {
        setOpen(true);
        onOpen?.();
      }}
      onClose={() => {
        setOpen(false);
        setInputValue('');
        onClose?.();
        // Blur only when focus is still inside this combobox.
        // Do not blur when focus has moved to another combobox (e.g. user clicked a different filter),
        // otherwise that combobox would close immediately.
        setTimeout(() => {
          const active = document.activeElement as HTMLElement;
          if (active && rootRef.current?.contains(active)) {
            active.blur();
          }
        }, 0);
      }}
      value={value}
      onChange={(_event, newValue) => {
        const hasSentinel = (newValue as unknown[]).some(isAddNewSentinel);
        if (hasSentinel) {
          commitTypedValue(inputValue.trim());
          setInputValue('');
        } else {
          onChange(newValue);
        }
      }}
      options={sortedItems}
      getOptionLabel={item => (isAddNewSentinel(item) ? inputValue : itemToString(item))}
      isOptionEqualToValue={(option, val) => {
        if (isAddNewSentinel(option) || isAddNewSentinel(val)) {
          return false;
        }
        return itemToString(option) === itemToString(val);
      }}
      disabled={disabled}
      loading={loading}
      getOptionDisabled={item => {
        if (isAddNewSentinel(item)) {
          return false;
        }
        return disableItem ? disableItem(item) : false;
      }}
      {...(loading && {
        popupIcon: <Spinner size="small" mr={2} />,
        forcePopupIcon: true,
      })}
      disableClearable
      disableCloseOnSelect
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (!searchable) {
          return;
        }
        if (reason === 'input') {
          setInputValue(newInputValue);
        } else if (reason === 'reset') {
          setInputValue(clearInputOnSelection ? '' : newInputValue);
        }
      }}
      filterOptions={(options, state) => {
        // Spread to ensure we never mutate the array reference that applyFiltering may return directly.
        const filtered = [...applyFiltering(options, state.inputValue)];
        // When allowAdditions is active and the typed value isn't already an exact match in either
        // the dropdown list or the selected values, append a sentinel that renderOption turns into
        // an "(Add new)" affordance.
        const trimmed = state.inputValue.trim();
        if (
          allowAdditions &&
          trimmed.length > 0 &&
          !filtered.some(item => itemToString(item) === trimmed) &&
          !value.some(item => itemToString(item) === trimmed) &&
          (!validateAddition || validateAddition(trimmed, value))
        ) {
          filtered.push(ADD_NEW_SENTINEL as unknown as Item);
        }
        return filtered;
      }}
      slotProps={{
        popper: {
          style: { minWidth: popperMinWidth, maxWidth: maxWidth || undefined },
          placement: 'bottom-start',
        } as any,
        listbox: {
          component: VirtualizedListbox,
          toggleGroup,
          showClearAll,
          clearAll,
          showSelectAll: canSelectAll,
          selectAll,
          maxHeight,
          selectAllText:
            typeof selectAllText === 'function'
              ? selectAllText(filteredItems, inputValue)
              : selectAllText,
          allSelected: (() => {
            // Filter out disabled items for accurate "all selected" indicator
            // Use filteredItems (not ref) to avoid stale state
            const selectableItems = disableItem
              ? filteredItems.filter(item => !disableItem(item))
              : filteredItems;
            return (
              selectableItems.length > 0 &&
              selectableItems.every(item => value.some(v => itemToString(v) === itemToString(item)))
            );
          })(),
        } as any,
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : label}
          placeholder={value.length === 0 ? placeholder : ''}
          error={invalid}
          required={required}
          onBlur={onBlur ? e => onBlur(e as React.FocusEvent<HTMLInputElement>) : undefined}
          onFocus={onFocus ? e => onFocus(e as React.FocusEvent<HTMLInputElement>) : undefined}
          slotProps={{
            inputLabel: {
              ...params.InputLabelProps,
              shrink: value.length > 0 || open,
            },
            input: {
              ...params.InputProps,
              readOnly: !searchable,
              sx: {
                gap: '4px',
                minHeight: '48px',
                flexWrap: renderContent ? 'nowrap' : 'wrap',
                overflow: 'hidden',
                // Shrink any value content (tags or custom renderContent elements) when space
                // is tight. Excludes the input itself, end adornment, and the outline fieldset.
                '& > :not(.MuiInputBase-input):not(.MuiAutocomplete-endAdornment):not(fieldset)': {
                  flexShrink: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                },
                // Input shrinks to fit alongside tags, but maintains minimum reachable width
                '& .MuiInputBase-input': {
                  flex: '1 0 40px',
                  minWidth: '40px',
                },
                ...(maxContainerHeight && {
                  maxHeight: maxContainerHeight,
                  overflow: 'auto',
                  flexWrap: 'wrap',
                }),
              },
              endAdornment: (
                <>
                  {inputEndContent && (
                    <Box
                      sx={{
                        pr: renderContent ? '5px' : '30px',
                        marginTop: renderContent ? '0px' : '-14px',
                      }}
                    >
                      {inputEndContent}
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              ...restProps,
              'aria-label': hideLabel ? label : undefined,
              name,
              ...(!searchable && {
                style: { ...params.inputProps?.style, caretColor: 'transparent' },
              }),
              onPaste: handlePaste,
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                if (allowAdditions && searchable) {
                  commitTypedValue(inputValue.trim());
                }
                setOpen(false);
                setInputValue('');
                if (params.inputProps?.onBlur) {
                  params.inputProps.onBlur(e as any);
                }
              },
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                handleKeyDown(e);
                if (!e.defaultPrevented && params.inputProps?.onKeyDown) {
                  params.inputProps.onKeyDown(e as any);
                }
              },
            },
          }}
        />
      )}
      renderValue={(tagValue, getItemProps) => {
        if (renderContent) {
          return renderContent({ itemToString, removeItem, value, isOpen: open, emptyText });
        }
        if (emptyText && value.length === 0 && !open) {
          return emptyText;
        }
        return tagValue.map((option, index) => {
          const { key } = getItemProps({ index });
          return (
            <Tag key={key} onRemove={() => removeItem(option)}>
              {itemToString(option)}
            </Tag>
          );
        });
      }}
      renderOption={(props, option, state) => {
        const { key: defaultKey, ...optionProps } = props;

        if (isAddNewSentinel(option)) {
          return (
            <Box
              component="li"
              {...optionProps}
              key="__add-new__"
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box component="span">{inputValue} (Add new)</Box>
              <Icon type="return-arrow" size="small" />
            </Box>
          );
        }

        const itemString = itemToString(option);
        const key = itemToKey ? itemToKey(option) : defaultKey;
        const isSelected = value.some(v => itemToString(v) === itemString);
        // Use filteredItems (not ref) to avoid stale state - same fix as allSelected
        const filtered = filteredItems;
        const isFirstInGroup = itemToGroup
          ? state.index === 0 ||
            (state.index < filtered.length &&
              itemToGroup(filtered[state.index]) !== itemToGroup(filtered[state.index - 1]))
          : false;

        let allGroupSelected: string | undefined;
        if (isFirstInGroup && itemToGroup) {
          const groupName = itemToGroup(option);
          const groupItems = filtered.filter(item => itemToGroup(item) === groupName);
          const selectableGroupItems = disableItem
            ? groupItems.filter(item => !disableItem(item))
            : groupItems;
          const every =
            selectableGroupItems.length > 0 &&
            selectableGroupItems.every(item =>
              value.some(v => itemToString(v) === itemToString(item)),
            );
          allGroupSelected = String(every);
        }

        return (
          <Box
            component="li"
            {...optionProps}
            key={key}
            data-first-in-group={isFirstInGroup || undefined}
            data-group-name={isFirstInGroup && itemToGroup ? itemToGroup(option) : undefined}
            data-all-group-selected={allGroupSelected}
          >
            <Box component="span">{renderMenuItem ? renderMenuItem(option) : itemString}</Box>
            <CheckmarkSvg checked={isSelected} />
          </Box>
        );
      }}
    />
  );
}

export const MultiCombobox = React.memo(React.forwardRef(MultiComboboxComponent)) as <T>(
  props: MultiComboboxProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
